import "@/App.css";
import { lazy, useEffect, useRef } from "react";
import { BrowserRouter, MemoryRouter, Routes, Route, useLocation } from "react-router-dom";
import Layout from "@/components/Layout";
import NotFound from "@/pages/NotFound";
import Home from "@/pages/Home";
import Redirect301 from "@/pages/Redirect301";
import { REDIRECTS } from "@/data/redirects";
import { SEO_PAGE_SLUGS } from "@/data/routes";
import { trackPageView } from "@/lib/analytics";

// Une page = un fichier JavaScript : chaque visiteur ne télécharge que la page consultée.
// Le HTML complet est pré-rendu au build (scripts/prerender.cjs).
const Metiers = lazy(() => import(/* webpackChunkName: "p-metiers" */ "@/pages/Metiers"));
const MetierPage = lazy(() => import(/* webpackChunkName: "p-metier" */ "@/pages/MetierPage"));
const Prix = lazy(() => import(/* webpackChunkName: "p-prix" */ "@/pages/Prix"));
const Attestation = lazy(() => import(/* webpackChunkName: "p-attestation" */ "@/pages/Attestation"));
const Guide = lazy(() => import(/* webpackChunkName: "p-guides" */ "@/pages/Guide"));
const PracticalGuide = lazy(() => import(/* webpackChunkName: "p-guide" */ "@/pages/PracticalGuide"));
const Sante = lazy(() => import(/* webpackChunkName: "p-sante" */ "@/pages/Sante"));
const Prevoyance = lazy(() => import(/* webpackChunkName: "p-prevoyance" */ "@/pages/Prevoyance"));
const Devis = lazy(() => import(/* webpackChunkName: "p-devis" */ "@/pages/Devis"));
const Contact = lazy(() => import(/* webpackChunkName: "p-contact" */ "@/pages/Contact"));
const Partenaires = lazy(() => import(/* webpackChunkName: "p-comparatif" */ "@/pages/Partenaires"));
const SeoLanding = lazy(() => import(/* webpackChunkName: "p-landing" */ "@/pages/SeoLanding"));
const LegalPage = lazy(() => import(/* webpackChunkName: "p-legal" */ "@/pages/LegalPage"));
const About = lazy(() => import(/* webpackChunkName: "p-about" */ "@/pages/About"));
const BlogIndex = lazy(() => import(/* webpackChunkName: "p-blog" */ "@/pages/BlogIndex"));
const BlogPost = lazy(() => import(/* webpackChunkName: "p-blogpost" */ "@/pages/BlogPost"));
const AdminLogin = lazy(() => import(/* webpackChunkName: "p-admin" */ "@/pages/AdminLogin"));
const Admin = lazy(() => import(/* webpackChunkName: "p-admin" */ "@/pages/Admin"));

function AnalyticsRouteTracker() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    // La page d'arrivée est mesurée par initAnalytics (après consentement).
    if (first.current) {
      first.current = false;
      return;
    }
    trackPageView(`${location.pathname}${location.search}`, document.title);
  }, [location.pathname, location.search]);
  return null;
}

function App({ url }) {
  const Router = url ? MemoryRouter : BrowserRouter;
  return (
    <Router initialEntries={url ? [url] : undefined}>
      {!url && <AnalyticsRouteTracker />}
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/metiers" element={<Metiers />} />
          <Route path="/metiers/:slug" element={<MetierPage />} />
          {Object.entries(REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<Redirect301 to={to} />} />
          ))}
          <Route path="/prix" element={<Prix />} />
          <Route path="/attestation" element={<Attestation />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/guide/:slug" element={<PracticalGuide />} />
          <Route path="/sante" element={<Sante />} />
          <Route path="/prevoyance" element={<Prevoyance />} />
          <Route path="/devis" element={<Devis />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/page/:page" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/comparatif-assurance-decennale" element={<Partenaires />} />
          {SEO_PAGE_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<SeoLanding slug={slug} />} />
          ))}
          <Route path="/cgu" element={<LegalPage kind="cgu" />} />
          <Route path="/confidentialite" element={<LegalPage kind="confidentialite" />} />
          <Route path="/mentions-legales" element={<LegalPage kind="mentions-legales" />} />
          <Route path="/informations-reglementaires" element={<LegalPage kind="informations-reglementaires" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
